import { Divider } from '@outdoorsyco/bonfire';
import React from 'react';
import { useIntl } from 'react-intl';

import Link from '@/components/switchback/Link/Link';
import { IPageCategory } from '@/services/getHelpPageData';

import ArticleCategories from '../static-pages/help/ArticleCategories';
import Articles from '../static-pages/help/Articles';
import HeadMeta from '../static-pages/help/HeadMeta';
import Hero from '../static-pages/help/Hero';
import Tags from '../static-pages/help/Tags';
import css from './HelpTemplate.module.css';

interface IArticleTags {
  id: string | null;
  name: string | null;
  slug: string | null;
}
interface IProps {
  title: string;
  content: string;
  slug: string;
  articles: IPageCategory[];
  articleCategories: IPageCategory[];
  articleTags: IArticleTags[];
  categoryTags: IArticleTags[];
}

interface ITagProps {
  tags: IArticleTags[];
  className?: string;
}

export const HelpNav: React.FC<ITagProps> = ({ tags, className }) => {
  const intl = useIntl();
  const HELP = intl.formatMessage({ defaultMessage: 'HELP', id: 'xwHzlY' });

  const tag = tags && tags.length ? tags[0] : null;
  if (!tag) return null;
  return (
    <div className={className}>
      <div>
        <Link legacy href={`/help`}>
          <a className={'text-gray-800 sm-caps-3'}>{HELP}</a>
        </Link>
      </div>
      {tag.name && (
        <>
          <div className={'text-gray-800 mx-2'}>/</div>
          <div>
            <Link legacy href={`/help/category/${tag.slug}/${tag.id}`}>
              <a className={'text-gray-800 sm-caps-3'}>{tag.name.toUpperCase()}</a>
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

const HelpTemplate = React.memo<IProps>(
  ({ content, title, articles, slug, articleCategories, articleTags, categoryTags }) => {
    const intl = useIntl();
    const TAGS = intl.formatMessage({ defaultMessage: 'TAGS', id: 'yJWdDO' });
    const CATEGORIES = intl.formatMessage({ defaultMessage: 'CATEGORIES', id: 'kW0X61' });
    const HELP_CATEGORIES = intl.formatMessage({ defaultMessage: 'Help Categories', id: 'meWUOO' });
    const POPULAR_ARTICLES = intl.formatMessage({
      defaultMessage: 'Popular Articles',
      id: 'G3D4NH',
    });

    return (
      <>
        <HeadMeta title={title} slug={slug} />
        <Hero />
        <div className="mx-auto xs:px-4 sm:px-8 md:px-12 lg:px-16 max-w-74">
          <HelpNav tags={categoryTags} className={'mt-20 flex'} />
          <h1
            dangerouslySetInnerHTML={{ __html: title }}
            className={`mt-4 mb-10 h3 text-gray-900 ${
              !categoryTags || !categoryTags.length ? 'mt-20' : ''
            }`}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-4 md:gap-x-20 ">
            <div className={'md:col-span-2 mb-10'}>
              <div
                className={`${css.content} mb-4`}
                dangerouslySetInnerHTML={{ __html: content }}
              />
              <div id="help" className={css.help} />
              {(articleTags && articleTags.length) || (categoryTags && categoryTags.length) ? (
                <Divider className={'my-9'} />
              ) : null}
              {articleTags && articleTags.length ? (
                <div className="flex my-2">
                  <div className={'sm-caps-3 py-2'}>{TAGS}</div>
                  <div className="flex-auto">
                    <Tags tags={articleTags} categoryTag />
                  </div>
                </div>
              ) : null}
              {categoryTags && categoryTags.length ? (
                <div className="flex my-2">
                  <div className={'sm-caps-3 py-2'}>{CATEGORIES}</div>
                  <div className="flex-auto">
                    <Tags tags={categoryTags} />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mb-10 max-w-17">
              {articleCategories && articleCategories.length ? (
                <>
                  <h6 className="mb-2 text-gray-900 h6">{HELP_CATEGORIES}</h6>
                  <ArticleCategories articleCategories={articleCategories} />
                  <Divider className={'my-9'} />
                </>
              ) : null}
              {articles && articles.length ? (
                <>
                  <h6 className="py-4 text-gray-900 h6">{POPULAR_ARTICLES}</h6>
                  <Articles articles={articles} />
                </>
              ) : null}
            </div>
          </div>
        </div>
      </>
    );
  },
);
HelpTemplate.displayName = 'HelpTemplate';
export default HelpTemplate;
